/**
 * Resolves auth status display (badge colors and labels) for orders, worklist rows, and J-codes.
 *
 * @module getAuthStatusOfMedicalPaOrder
 */
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { BadgeWithIconProps } from "../data-model/badgeWithIconProps";
import { FinalWorklistDataResponse } from "../data-model/finalWorklistModel";
import {
  AuthOnFile,
  JCodeDetails,
  MedicalPaOrder,
} from "../data-model/medicalPaOrdersModel";
import {
  getBadgePropsForAuthStatus,
  MedicalPaOrdersAuthStatus,
} from "../enums/medicalPaOrdersAuthStatus";

/**
 * Badge props for the order's master `auth_on_file.auth_status`.
 *
 * @param medicalPaOrder - Full medical PA order
 * @param authStatusOptions - Payer-specific status → style mappings from Redux/config
 * @returns {@link BadgeWithIconProps}; falls back to "Not Available" when unmatched
 */
export const getAuthStatusOfMedicalPaOrder = (
  medicalPaOrder: MedicalPaOrder,
  authStatusOptions: AuthStatusOptionModel[],
): BadgeWithIconProps => {
  const authStatus = medicalPaOrder?.auth_on_file?.auth_status;

  return (
    getBadgePropsForAuthStatus(authStatusOptions, authStatus) ?? {
      id: MedicalPaOrdersAuthStatus.NotAvailable,
      text: "Not Available",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    }
  );
};

/**
 * Same as {@link getAuthStatusOfMedicalPaOrder} but reads `auth_status` from a standalone {@link AuthOnFile}.
 *
 * @param authOnFileDetails - Postgres-shaped auth-on-file payload
 * @param authStatusOptions - Status → badge style mappings
 * @returns {@link BadgeWithIconProps}; falls back to "Not Available" when unmatched
 */
export const getAuthStatusOfMedicalPaOrderFromAuthOnFile = (
  authOnFileDetails: AuthOnFile,
  authStatusOptions: AuthStatusOptionModel[],
): BadgeWithIconProps => {
  const authStatus = authOnFileDetails?.auth_status;

  return (
    getBadgePropsForAuthStatus(authStatusOptions, authStatus) ?? {
      id: MedicalPaOrdersAuthStatus.NotAvailable,
      text: "Not Available",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    }
  );
};

/**
 * Badge props for a final-worklist row's flat `auth_status` field.
 *
 * @param medicalPaOrder - Final worklist API row
 * @param authStatusOptions - Status → badge style mappings
 * @returns {@link BadgeWithIconProps}; falls back to "Not Available" when unmatched
 */
export const getAuthStatusOfFinalWorklist = (
  medicalPaOrder: FinalWorklistDataResponse,
  authStatusOptions: AuthStatusOptionModel[],
): BadgeWithIconProps => {
  const authStatus = medicalPaOrder?.auth_status;
  return (
    getBadgePropsForAuthStatus(authStatusOptions, authStatus) ?? {
      id: MedicalPaOrdersAuthStatus.NotAvailable,
      text: "Not Available",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    }
  );
};

/**
 * Badge props for a single line-level J-code `auth_status`.
 *
 * @param jCode - One J-code row from auth entries
 * @param authStatusOptions - Status → badge style mappings
 * @returns {@link BadgeWithIconProps}; "Not Available" when `auth_status` is missing
 */
export const getAuthStatusOfOneJCode = (
  jCode: JCodeDetails,
  authStatusOptions: AuthStatusOptionModel[],
): BadgeWithIconProps => {
  const authStatus = jCode?.auth_status;

  if (!authStatus) {
    return {
      id: MedicalPaOrdersAuthStatus.NotAvailable,
      text: "Not Available",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    };
  }

  return (
    getBadgePropsForAuthStatus(authStatusOptions, authStatus) ?? {
      id: MedicalPaOrdersAuthStatus.NotAvailable,
      text: "Not Available",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    }
  );
};

/**
 * Plain-text auth status label for spreadsheets or simple UI.
 *
 * @param medicalPaOrder - Full medical PA order
 * @param authStatusOptions - Status → badge style mappings
 * @returns `text` field from {@link getAuthStatusOfMedicalPaOrder}, or empty string
 */
export const getAuthStatusTextOfMedicalPaOrder = (
  medicalPaOrder: MedicalPaOrder,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return (
    getAuthStatusOfMedicalPaOrder(medicalPaOrder, authStatusOptions)?.text ?? ""
  );
};

/**
 * Alias of {@link getAuthStatusOfMedicalPaOrder} for table column renderers.
 *
 * @param medicalPaOrder - Full medical PA order
 * @param authStatusOptions - Status → badge style mappings
 * @returns {@link BadgeWithIconProps}
 */
export const getAuthStatusForTables = (
  medicalPaOrder: MedicalPaOrder,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return getAuthStatusOfMedicalPaOrder(medicalPaOrder, authStatusOptions);
};

export const getLabelForAuthStatus = (
  authStatus: string,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return (
    getBadgePropsForAuthStatus(authStatusOptions, authStatus)?.text ??
    "Not Available"
  );
};
